import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { HdLocksKeysPanelLinkComponent } from './hd-locks-keys-panel-link.component';
import { HdLocksKeysReadingPanelId } from './hd-locks-keys-reading-panels';
import { parseHdLocksKeysSourceRefs } from './hd-locks-keys-source-refs';

interface LocksKeysSegmentVm {
  readonly text: string;
  /** Voetnootnummer naar de bronnenlijst; de tooltip toont de bron(nen) zelf. */
  readonly isRef: boolean;
  readonly refTitle?: string;
  /** Label "berekend" (`{calc}` in de tekst). */
  readonly isCalc: boolean;
}

type LocksKeysTextVm = readonly LocksKeysSegmentVm[];

interface LocksKeysFactVm {
  readonly label: LocksKeysTextVm;
  readonly value: LocksKeysTextVm;
}

interface LocksKeysTableVm {
  readonly caption: LocksKeysTextVm;
  readonly columns: readonly string[];
  readonly rows: readonly (readonly LocksKeysTextVm[])[];
}

interface LocksKeysTimelineVm {
  readonly when: string;
  readonly what: LocksKeysTextVm;
}

interface LocksKeysSectionVm {
  readonly title: string;
  readonly facts: readonly LocksKeysFactVm[];
  readonly paragraphs: readonly LocksKeysTextVm[];
  readonly table: LocksKeysTableVm | undefined;
  readonly timeline: readonly LocksKeysTimelineVm[];
  readonly items: readonly LocksKeysTextVm[];
  readonly itemsAsBullets: boolean;
  readonly closing: LocksKeysTextVm;
  readonly note: LocksKeysTextVm;
  readonly sources: readonly string[];
}

/**
 * Eén sectie van de Locks & Keys-leesteksten, gedeeld door het intro-paneel
 * en de vervolgpanelen ("Mens na 2027", biologie, Rave en Penta, nodale
 * omgeving), zodat elk nieuw blok maar één keer gebouwd wordt.
 *
 * Sectiemodel onder `<prefix>` in de `human-design/locks-keys`-scope, alles
 * optioneel behalve `title`, in deze weergavevolgorde:
 * - `facts`: kernfeitenstrip, `{ "0": [label, waarde], ... }`
 * - `paragraphs`
 * - `table`: `{ caption, columns: [...], rows: { "0": [cel, ...], ... } }`
 * - `timeline`: verticale tijdlijn, `{ "0": [wanneer, wat], ... }`
 * - `items` (genummerd, of met `itemsStyle: "bullets"` ongenummerd)
 * - `closing`, `note` (notitiekader), `sources` (genummerde bronnenlijst)
 *
 * In elke tekst (ook in cellen) wordt `[n]` een voetnoot naar `sources` en
 * `{calc}` een label "berekend". Rijen zijn objecten met numerieke sleutels
 * en lijsten van strings, nooit lijsten van objecten (flatten-valkuil).
 */
@Component({
  selector: 'hd-locks-keys-section',
  imports: [SharedModule, HdLocksKeysPanelLinkComponent],
  templateUrl: './hd-locks-keys-section.component.html',
  styleUrl: './hd-locks-keys-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdLocksKeysSectionComponent {
  /** Volledige i18n-prefix van de sectie, bv. `intro.clock` of `postShift.timeline`. */
  readonly prefix = input.required<string>();
  /** Sectiesleutel voor de test-id's. */
  readonly sectionKey = input.required<string>();
  readonly testIdPrefix = input('locks-keys-reading');
  /** Optionele doorlink naar een vervolgpaneel onderaan de sectie. */
  readonly linkPanel = input<HdLocksKeysReadingPanelId | undefined>(undefined);

  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly sourcesLabel = computed(() => this.gateInfo.locksKeysTextIn('reading.sourcesLabel', this.lang()));
  protected readonly calcLabel = computed(() => this.gateInfo.locksKeysTextIn('reading.calcLabel', this.lang()));
  protected readonly calcTitle = computed(() => this.gateInfo.locksKeysTextIn('reading.calcTitle', this.lang()));

  protected readonly section = computed<LocksKeysSectionVm>(() => {
    const prefix = this.prefix();
    const lang = this.lang();
    const textOf = (key: string) => this.gateInfo.locksKeysTextIn(`${prefix}.${key}`, lang);
    const listOf = (key: string) => this.gateInfo.locksKeysListIn(`${prefix}.${key}`, lang);
    const rowsOf = (key: string) => this.gateInfo.locksKeysRowsIn(`${prefix}.${key}`, lang);
    const sources = listOf('sources');
    const text = (value: string): LocksKeysTextVm => toText(value, sources);

    const tableRows = rowsOf('table.rows');
    return {
      title: textOf('title'),
      facts: rowsOf('facts').map(([label = '', value = '']) => ({ label: text(label), value: text(value) })),
      paragraphs: listOf('paragraphs').map(text),
      table: tableRows.length === 0
        ? undefined
        : {
            caption: text(textOf('table.caption')),
            columns: listOf('table.columns'),
            rows: tableRows.map(row => row.map(text)),
          },
      timeline: rowsOf('timeline').map(([when = '', what = '']) => ({ when, what: text(what) })),
      items: listOf('items').map(text),
      itemsAsBullets: textOf('itemsStyle') === 'bullets',
      closing: text(textOf('closing')),
      note: text(textOf('note')),
      sources,
    };
  });
}

function toText(value: string, sources: readonly string[]): LocksKeysTextVm {
  return parseHdLocksKeysSourceRefs(value).map(segment =>
    segment.refs == null
      ? { text: segment.text, isRef: false, isCalc: segment.calc === true }
      : {
          text: segment.text,
          isRef: true,
          isCalc: false,
          refTitle: segment.refs
            .map(ref => sources[ref - 1])
            .filter(source => source != null)
            .join('\n'),
        },
  );
}
